import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import { toast } from 'react-hot-toast';
import { useAuth } from './AuthContext';
import { SOCKET_URL } from '../services/api';
import { authStorage } from '../utils/authStorage';

interface ServiceNotification {
    id: string;
    tableId: string;
    tableNumber: string;
    type: 'waiter' | 'bill';
    message: string;
    timestamp: Date;
}

interface SocketContextType {
    socket: Socket | null;
    connected: boolean;
    /** Increments chaque fois que le socket se reconnecte après une déconnexion.
     *  Les composants peuvent l'utiliser comme dépendance useEffect pour rafraîchir leurs données. */
    reconnectCount: number;
    serviceNotifications: ServiceNotification[];
    clearServiceNotification: (tableId: string) => void;
}

const SocketContext = createContext<SocketContextType>({
    socket: null,
    connected: false,
    reconnectCount: 0,
    serviceNotifications: [],
    clearServiceNotification: () => { }
});

// eslint-disable-next-line react-refresh/only-export-components
export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }: { children: ReactNode }) => {
    const { user } = useAuth();
    // useRef évite d'appeler setState dans le corps de l'effet (règle react-hooks/set-state-in-effect)
    const socketRef = useRef<Socket | null>(null);
    const [socket, setSocket] = useState<Socket | null>(null);
    const [connected, setConnected] = useState(false);
    const [reconnectCount, setReconnectCount] = useState(0);
    const wasConnectedRef = useRef(false);
    const [serviceNotifications, setServiceNotifications] = useState<ServiceNotification[]>([]);

    const clearServiceNotification = (tableId: string) => {
        setServiceNotifications(prev => prev.filter(n => n.tableId !== tableId));
    };

    useEffect(() => {
        const token = authStorage.getToken();

        if (token && user) {
            // Réinitialiser l'état de connexion à chaque nouveau socket (changement d'utilisateur)
            wasConnectedRef.current = false;

            const newSocket = io(SOCKET_URL, {
                auth: { token },
                // Also sent as a query param (harmless/ignored by the official Socket.IO
                // server, which already reads `auth.token`) — local-server-android's
                // netty-socketio server can only validate at the Engine.IO handshake layer
                // (HTTP headers/query params), before the Socket.IO CONNECT packet carrying
                // `auth` even arrives. See local-server-android's LocalSocketService.kt.
                query: { token },
                transports: ['websocket', 'polling'], // Prefer websocket
                upgrade: true
            });

            newSocket.on('connect', () => {
                setConnected(true);

                // Reconnexion détectée (pas la connexion initiale) → rafraîchir les données
                if (wasConnectedRef.current) {
                    setReconnectCount(c => c + 1);
                }
                wasConnectedRef.current = true;

                // console.log("Socket connected:", newSocket.id);

                // Robust Restaurant ID Resolution
                let restaurantIdToJoin = user.restaurant_id;

                if (!restaurantIdToJoin) {
                    // Check employees
                    if (user.employees && Array.isArray(user.employees) && user.employees.length > 0) {
                        const emp = user.employees[0];
                        if (emp.restaurant_id) {
                            restaurantIdToJoin = emp.restaurant_id;
                        }
                    }
                    // Check partners
                    else if (user.partners) {
                        const partnersList = Array.isArray(user.partners) ? user.partners : [user.partners];
                        for (const p of partnersList) {
                            if (p.restaurants && Array.isArray(p.restaurants) && p.restaurants.length > 0) {
                                restaurantIdToJoin = p.restaurants[0].id;
                                break;
                            }
                        }
                    }
                }

                // console.log("User Data for Socket:", user);
                // console.log("Resolved Restaurant ID:", restaurantIdToJoin);

                // Join restaurant room if user belongs to one
                if (restaurantIdToJoin) {
                    // console.log(`[Socket] Joining restaurant room: ${restaurantIdToJoin}`);
                    newSocket.emit('join_restaurant', restaurantIdToJoin.toString());
                }

                // New online order notifications
                newSocket.off('new_order');
                newSocket.on('new_order', (_data: { orderId: string; orderNumber: string; status: string }) => {
                    try {
                        const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
                        audio.play().catch(e => console.log('Audio play failed:', e));
                    } catch (e) {
                        console.error('Audio error:', e);
                    }
                    toast.error(`🛵 Nouvelle commande en ligne #${_data.orderNumber || ''}`, {
                        duration: 10000,
                        style: {
                            borderRadius: '12px',
                            background: '#ef4444',
                            color: '#fff',
                            fontSize: '16px',
                            fontWeight: 'bold',
                        }
                    });
                });

                // Digital Menu Listeners
                newSocket.off('table_notification');
                newSocket.on('table_notification', (data: Omit<ServiceNotification, 'id'> & { timestamp?: string }) => {
                    console.log('[Socket] Table Notification:', data);

                    // Update active notifications
                    setServiceNotifications(prev => {
                        // Avoid duplicates for the same table
                        const filtered = prev.filter(n => n.tableId !== data.tableId);
                        return [...filtered, {
                            id: Math.random().toString(36).substr(2, 9),
                            tableId: data.tableId,
                            tableNumber: data.tableNumber,
                            type: data.type,
                            message: data.message,
                            timestamp: new Date(data.timestamp || new Date())
                        }];
                    });

                    // Notification Sound
                    try {
                        const audio = new Audio('https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3');
                        audio.play().catch(e => console.log('Audio play failed:', e));
                    } catch (e) {
                        console.error('Audio error:', e);
                    }

                    toast(data.message, {
                        icon: data.type === 'waiter' ? '🔔' : '🧾',
                        duration: 6000,
                        style: {
                            borderRadius: '12px',
                            background: '#333',
                            color: '#fff',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            borderLeft: '4px solid #f97316'
                        }
                    });
                });
            });

            newSocket.on('disconnect', () => {
                setConnected(false);
            });

            // Stocker dans le ref puis mettre à jour le state via un microtask
            // → évite l'appel setState synchrone dans le corps de l'effet
            socketRef.current = newSocket;
            Promise.resolve().then(() => setSocket(newSocket));

            return () => {
                newSocket.disconnect();
                socketRef.current = null;
            };
        } else {
            socketRef.current = null;
            Promise.resolve().then(() => {
                setSocket(null);
                setConnected(false);
            });
        }
    }, [user]);

    return (
        <SocketContext.Provider value={{ socket, connected, reconnectCount, serviceNotifications, clearServiceNotification }}>
            {children}
        </SocketContext.Provider>
    );
};
